import { useEffect, useRef } from 'react';

/**
 * Chiusa la pagina dei risultati, l'arena e' archiviata.
 *
 * `status: 'finished'` non e' uno stato di passaggio: e' l'interruttore su cui
 * `App.tsx:989-1013` decide di mandare al podio chi ha giocato. Finche' resta acceso
 * con la sua arena in canna, la PROSSIMA arena che si apre non e' la propria:
 * `arenaInStoreId` comanda su `selectedArenaId` (`App.tsx:597`) e riporta
 * all'arena vecchia, che essendo 'finished' rimanda al suo podio. Si toccava
 * un'arena e se ne apriva un'altra, per sempre.
 *
 * Prima non si vedeva perche' a spegnerlo ci pensava, per caso, l'azzeramento
 * allo smontaggio del dettaglio; ora quello smontaggio e' proprio la strada per
 * il podio e non puo' piu' azzerare niente (`ArenaDetailPage.tsx:128-152`).
 *
 * La regola guarda dove si finisce, non da che porta si esce: uscire verso
 * l'elenco, la Home o il gesto indietro sono la stessa cosa.
 *
 * ⚠️ Con DUE schermate escluse, e non sono un dettaglio. `reset()` e' la scopa
 * grossa: azzera `currentArena`, l'elenco, il contesto della chat, e
 * soprattutto **incrementa la generazione delle richieste**
 * (`arena-store.ts:1238`). Su quelle due distruggerebbe stato che nessuno
 * rimette — ed e' l'invariante che tiene insieme l'elenco, non il fatto che si
 * ricarichino: le ragioni sono DUE e diverse.
 *
 * - `arena-detail` **se la ricarica**, ma nell'istante sbagliato. Dal podio
 *   l'indietro ci atterra per un giro, e in quel commit React esegue gli
 *   effetti dei figli PRIMA di quelli del genitore: `ArenaDetailPage` ha gia'
 *   chiesto l'arena quando questa guardia gira. Archiviare li' annullava quella
 *   richiesta a meta' («risposta in ritardo», `arena-store.ts:585-588`) e
 *   lasciava la pagina su «Caricamento…» per sempre, perche' l'effetto di
 *   montaggio non si ripete. Misurato, non dedotto:
 *   `arena-podio-rientro.test.tsx`.
 *
 *   ⚠️ Delle due strade per il podio ci passa **una sola**, e vale la pena
 *   sapere quale. Se l'arena chiude mentre la GUARDI, il podio prende il posto
 *   della voce del dettaglio (`navigateReplace`, `App.tsx:1012`) e l'indietro
 *   va dritto all'elenco: qui non si arriva. Se chiude mentre GIOCHI, il podio
 *   prende il posto della damiera e sotto c'e' ancora il dettaglio: l'indietro
 *   ce lo trova, lo disegna, e l'instradamento lo rimanda subito su. E' quel
 *   giro — breve, e invisibile a schermo — che questa esclusione protegge.
 *   Misurato contando i disegni della pagina:
 *   `debug/01-app-navigation.test.tsx`, «dal podio dell'arena chiusa in partita
 *   l'indietro passa dal dettaglio».
 * - `arena-game` non ricarica **niente**: `ArenaGame` non chiama `loadArena`,
 *   e lo stato della partita — damiera, orologio, avversario — vive solo nello
 *   store. Azzerarlo lo butterebbe via senza che nessuno lo rimetta, e
 *   `App.tsx:964` non riscatta, perche' fra gli stati che riportano al podio
 *   'idle' non c'e': resterebbe una damiera vuota, gemella della pagina che
 *   carica per sempre.
 *
 * L'alternativa scartata era un'azione mirata che spegnesse il solo verdetto
 * lasciando in piedi `currentArena`: non basta, perche' a dirottare l'arena
 * successiva e' `App.tsx:597`, che guarda `currentArena` e non `status`.
 *
 * E archivia il SOLO verdetto: `arenaStatus === 'finished'` e' parte della
 * regola, non un di piu'. Oggi con l'arena ancora viva su questa pagina non ci
 * si passa — l'instradamento manda al podio soltanto chi e' iscritto a un'arena
 * 'finished' (`App.tsx:989`) — ma la condizione resta, ed e' una cintura di
 * sicurezza che vale la sua riga: `reset()` su un'arena viva butterebbe fuori
 * dalla coda chi la sta ancora giocando, e non c'e' nessuna ragione per cui
 * questa guardia debba fidarsi di un altro file per non farlo.
 */

/**
 * Le schermate su cui `reset()` distruggerebbe stato che nessuno rimette: qui
 * non si archivia. Il dettaglio perche' la sua richiesta e' gia' in volo, la
 * damiera perche' la partita vive solo nello store — vedi sopra, sono due
 * ragioni diverse sotto la stessa invariante.
 */
const SCHERMI_DA_NON_AZZERARE = ['arena-detail', 'arena-game'];

export function useArenaResultsCleanup(
  arenaStatus: string,
  screen: string,
  reset: () => void,
): void {
  const precedente = useRef(screen);
  useEffect(() => {
    const prima = precedente.current;
    precedente.current = screen;
    if (
      prima === 'arena-results' &&
      screen !== 'arena-results' &&
      !SCHERMI_DA_NON_AZZERARE.includes(screen) &&
      arenaStatus === 'finished'
    ) {
      reset();
    }
  }, [arenaStatus, screen, reset]);
}
